"use client";

import { useQuery } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import { GalleryVerticalEnd } from "lucide-react";
import {
	type StoreFromAPI,
	getStoresAdapter,
} from "@/features/stores/adapters/get-stores";

export interface Store {
	id: string;
	name: string;
	slug: string;
	logo?: string;
	favicon?: string;
	plan?: string;
	role?: "owner" | "admin" | "manager" | "support" | "viewer";
}

export interface StoreWithIcon {
	id: string;
	name: string;
	slug: string;
	logo: LucideIcon;
	favicon?: string;
	plan: string;
	role: "owner" | "admin" | "manager" | "support" | "viewer";
}

function mapStoreToSwitcherFormat(store: Store): StoreWithIcon {
	return {
		id: store.id,
		name: store.name,
		slug: store.slug,
		logo: GalleryVerticalEnd,
		favicon: store.favicon,
		plan: store.plan || "Free",
		role: store.role || "owner",
	};
}

function mapStoresFromApi(stores: StoreFromAPI[]): StoreWithIcon[] {
	const storesAsStore: Store[] = stores.map((store) => ({
		id: store.id,
		name: store.name,
		slug: store.slug,
		favicon: store.faviconUrl,
		plan: undefined,
		role: store.role || "owner",
	}));

	return storesAsStore.map(mapStoreToSwitcherFormat);
}

/**
 * Canonical query for the signed-in user's stores. The cache holds the raw
 * `StoreFromAPI[]` under `["stores"]` so the route `beforeLoad` (which needs the
 * raw shape) and `useStores` (which maps for the switcher) share a single entry.
 */
export const storesQueryOptions = {
	queryKey: ["stores"] as const,
	queryFn: getStoresAdapter,
};

export function useStores(options?: { initialStores?: StoreFromAPI[] }) {
	const {
		data: storesData = [],
		isLoading,
		error,
	} = useQuery({
		...storesQueryOptions,
		initialData: options?.initialStores,
		select: mapStoresFromApi,
	});

	return {
		stores: storesData,
		isLoading,
		error: error
			? error instanceof Error
				? error
				: new Error("Failed to load stores")
			: null,
	};
}
